/**
 * GA4 Analytics Types
 */

export enum GA4EventName {
  PAGE_VIEW = 'page_view',
  LOGIN = 'login',
  LOGOUT = 'logout',
  SIGN_UP = 'sign_up',
  SEARCH = 'search',
  SELECT_CONTENT = 'select_content',
  VIEW_ITEM = 'view_item',
  SELECT_ITEM = 'select_item',
  AFFILIATE_CLICK = 'affiliate_click',
  MODAL_OPEN = 'modal_open',
  MODAL_CLOSE = 'modal_close',
  ERROR = 'error',
  ERROR_API = 'error_api',
  CTA_CLICK = 'cta_click',
  HERO_CARD_CLICK = 'hero_card_click',
  EXTERNAL_LINK_CLICK = 'external_link_click',
  TRAVEL_SERVICES_FUNNEL = 'travel_services_funnel',
  TRAVEL_SERVICES_FUNNEL_LOGIN = 'travel_services_funnel_login',
}

export enum GA4Flow {
  FLIGHTS = 'flights',
  HOTELS_ATTRACTIONS = 'hotels_attractions',
}

export enum GA4Action {
  FLOW_SELECTED = 'flow_selected',
  IDENTIFICATION_START = 'identification_start',
  CONTINUE = 'continue',
  SUCCESS = 'success',
  TYPE_SELECTED = 'type_selected',
  SEARCH_FLIGHTS = 'search_flights',
  SORT_APPLIED = 'sort_applied',
  FILTER_PANEL_OPENED = 'filter_panel_opened',
  FILTER_APPLIED = 'filter_applied',
  FLIGHT_COMPARE_CLICK = 'flight_compare_click',
  FLIGHT_MORE_INFO_CLICK = 'flight_more_info_click',
  FLIGHT_SHOW_INFO_CLICK = 'flight_show_info_click',
  FLIGHT_SUPPLIER_CLICK = 'flight_supplier_click',
  LOGOUT = 'logout',
  PROMO_BANNER_CLICKED = 'promo_banner_clicked',
  NAVIGATE_BACK = 'navigate_back',
  HOTEL_SUPPLIER_CLICK = 'hotel_supplier_click',
  ATTRACTION_SUPPLIER_CLICK = 'attraction_supplier_click',
  CONTINUE_AFTER_ACCEPTANCE = 'continue after acceptance',
  CLICK_FOR_MORE_DETAILS = 'click for more details',
}

export enum GA4Method {
  SMS = 'sms',
  EMAIL = 'email',
}

export enum GA4TripType {
  ONE_WAY = 'one_way',
  ROUND_TRIP = 'round_trip',
}

export enum GA4HotelsType {
  HOTELS = 'hotels',
  ATTRACTIONS = 'attractions',
}

export enum GA4ErrorType {
  INVALID_ID = 'invalid_id',
  INVALID_PHONE = 'invalid_phone',
  INVALID_EMAIL = 'invalid_email',
  INVALID_CODE = 'invalid_code',
  CODE_EXPIRED = 'code_expired',
  AN_ERROR_OCCURRED = 'an_error_occurred',
  MAX_ATTEMPTS = 'max_attempts',
}

export enum GA4Step {
  HOME = 'home',
  OTP_1 = 'otp 1',
  OTP_2 = 'otp 2',
  OTP_3 = 'otp 3',
  OTP_2_ACCEPTANCE_POPUP = 'otp 2 - acceptance popup',
  FLIGHT_SEARCH_RESULTS_POPUP = 'flight_search_results_popup',
  HOTELS_ATTRACTIONS_TIMER_POPUP = 'hotels_attractions_timer_popup',
  FLIGHT_SEARCH = 'flight_search',
  FLIGHT_SEARCH_RESULTS = 'flight_search_results',
  HOTELS_ATTRACTIONS_RESULTS = 'hotels_attractions_results',
  HOTELS_ATTRACTIONS_RESULTS_POPUP = 'hotels_attractions_results_popup',
}

export interface GA4Config {
  measurementId: string;
  sendPageViews?: boolean;
}

export interface CustomEventParams {
  [key: string]: string | number | boolean | undefined;
}

export type EventCategory = 
  | 'engagement'
  | 'navigation'
  | 'authentication'
  | 'search'
  | 'booking'
  | 'error'
  | 'conversion';

export interface BaseEventParams {
  event_category?: EventCategory;
  event_label?: string;
  value?: number;
}

export interface PageViewParams {
  page_path?: string;
  page_title?: string;
  page_location?: string;
}

export interface UserProperties {
  user_id?: string;
  user_type?: string;
  card_type?: string;
  language?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  variation?: string;
  cid_enc?: string;
  [key: string]: string | undefined;
}

export interface LoginEventParams extends BaseEventParams {
  method?: string;
}

export interface SearchEventParams extends BaseEventParams {
  search_term?: string;
  origin?: string;
  destination?: string;
  departure_date?: string;
  return_date?: string;
  passengers?: number;
  trip_type?: 'one_way' | 'round_trip';
}

export interface SelectContentParams extends BaseEventParams {
  content_type?: string;
  item_id?: string;
}

export interface AffiliateClickParams extends BaseEventParams {
  affiliate_name?: string;
  affiliate_id?: string | number;
  destination_url?: string;
}

export interface FlightResultParams extends BaseEventParams {
  carrier?: string;
  price?: number;
  currency?: string;
  flight_number?: string;
  origin?: string;
  destination?: string;
}

export interface ErrorEventParams extends BaseEventParams {
  error_message?: string;
  error_code?: string;
  error_type?: string;
}

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}
